import { useEffect, useState } from 'react';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../../hooks/useAuth';
import Logo from '../common/Logo';
import { providersAPI } from '../../api/providers';

const PLAN_STYLES = {
  ORDINARY: { label: 'Ordinary plan', bg: 'var(--gray-3)', color: 'var(--gray-11)' },
  ADVANCED: { label: 'Advanced plan', bg: 'var(--orange-2)', color: 'var(--orange-10)' },
  PREMIUM: { label: 'Premium plan', bg: '#ede9fe', color: '#7c3aed' },
};

const DashboardTopBar = () => {
  const { user, isProvider } = useAuth();
  const [plan, setPlan] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isProvider) return;
    providersAPI
      .getMyProfile()
      .then((profile) => {
        const subscribed = profile?.subscription?.plan?.toUpperCase();
        if (PLAN_STYLES[subscribed]) setPlan(subscribed);
      })
      .catch(() => {});
  }, [isProvider]);

  const planStyle = plan ? PLAN_STYLES[plan] : null;

  return (
    <header
      className="sticky top-0 z-40 w-full transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255,255,255,0.92)' : 'var(--color-background)',
        backdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
        boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.06)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.04)' : '1px solid var(--gray-4)',
      }}
    >
      <div className="flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo size="small" state={{ stayOnLanding: true }} />

        {user && (
          <div className="flex items-center gap-3 min-w-0">
            <div className="hidden sm:flex flex-col items-end min-w-0">
              <span
                className="flex items-center gap-1 max-w-[240px] text-sm font-medium"
                style={{
                  color: 'var(--gray-12)',
                  fontFamily: "'Poppins', sans-serif",
                }}
                title={user.name || user.email}
              >
                <span className="truncate">{user.name || user.email}</span>
                {plan === 'PREMIUM' && (
                  <CheckBadgeIcon
                    className="w-[18px] h-[18px] shrink-0"
                    style={{ color: '#F05A35' }}
                    aria-label="Verified provider"
                  />
                )}
              </span>
              {planStyle && (
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    padding: '1px 8px',
                    borderRadius: '999px',
                    backgroundColor: planStyle.bg,
                    color: planStyle.color,
                    fontFamily: "'Poppins', sans-serif",
                    marginTop: '2px',
                  }}
                >
                  {planStyle.label}
                </span>
              )}
            </div>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name || 'User'}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '1px solid var(--gray-4)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--orange-3)',
                  color: 'var(--orange-10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default DashboardTopBar;
